import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineUser,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineCalendar,
  HiOutlineMapPin,
  HiOutlineTag,
  HiCake,
  HiOutlineIdentification,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineXMark,
  HiOutlineLockClosed,
  HiOutlineCamera,
  HiOutlineSparkles,
  HiOutlineHome, // Added for address type
  HiOutlineBriefcase, // Added for address type
} from "react-icons/hi2";
import { RiGenderlessLine } from "react-icons/ri";
import toast, { Toaster } from "react-hot-toast";
import SummaryApi from "../common";
import thyrocareImg from "../assets/Thyrocare.jpg";
import { useLocation } from "react-router-dom"; // 1. Import useLocation
import { VscReferences } from "react-icons/vsc";
import UserProfileAddress from "./UserProfileAddress";
const UserProfile = () => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]); // New state for addresses
  const [loading, setLoading] = useState(true);
  const [addressLoading, setAddressLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [imgError, setImgError] = useState(false); // To handle image load failure
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  // Rename addressForm to formData to fix the ESLint errors
  const [formData, setFormData] = useState({
    user_name: "",
    user_mobile: "",
    flat_number: "",
    street_name: "",
    land_mark: "",
    address_type: "home",
    city: "",
    state: "",
    pincode: "",
    address: "",
    is_default: 0,
  });
  // Temporarily change your state to this to test the UI:
  const [userSubscriptions, setUserSubscriptions] = useState([
    { is_active: 1, is_used: 0 },
  ]);

  const getFullImageUrl = (imageName) => {
    if (!imageName) return null;

    // 1. Return immediately if it's already a full URL or a local preview
    if (imageName.startsWith("http") || imageName.startsWith("blob:")) {
      return imageName;
    }

    // 2. Take the base URL from your API config
    let baseUrl = SummaryApi.userProfileImage.url.trim();

    // 3. REMOVE "/api" and any double "/uploads"
    // This replaces "/api" with nothing and reduces "//" to "/"
    baseUrl = baseUrl.replace("/api", "").replace(/\/+$/, "");

    // 4. Clean the imageName (remove leading slashes)
    const cleanName = imageName.trim().replace(/^\/+/, "");

    // 5. Combine Base + Name
    let finalUrl = `${baseUrl}/${cleanName}`;

    // 6. FIX: Global cleanup for double segments
    finalUrl = finalUrl
      .replace(/\/uploads\/uploads\//g, "/uploads/")
      .replace(/([^:])\/\//g, "$1/");

    // 7. Ensure "user_profiles" subfolder is present
    if (!finalUrl.includes("user_profiles/")) {
      finalUrl = finalUrl.replace("/uploads/", "/uploads/user_profiles/");
    }

    console.log("🚀 Final Clean URL:", finalUrl);
    return finalUrl;
  };
  const getUserId = useCallback(() => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return null;
    try {
      const parsed = JSON.parse(sessionData);
      return parsed.user_id || parsed.id || parsed;
    } catch (e) {
      return sessionData;
    }
  }, []);
  const fetchSubscriptions = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;

    try {
      // Note: Ensure SummaryApi.getUserSubscription exists in your common/index.js
      const response = await fetch(SummaryApi.getUserSubscription.url, {
        method: SummaryApi.getUserSubscription.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });

      const result = await response.json();
      if (result.status) {
        // Assuming result.data is the array of subscriptions
        setUserSubscriptions(result.data || []);
      }
    } catch (error) {
      console.error("Failed to load subscriptions", error);
    }
  }, [getUserId]);
  const fetchProfile = useCallback(async () => {
    const userId = getUserId();
    if (!userId) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(SummaryApi.userDetailsById.url, {
        method: SummaryApi.userDetailsById.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await response.json();
      if (result.status) {
        setUser(result.data);
        setImgError(false); // Reset error state on new fetch
      }
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [getUserId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProfile();
    fetchSubscriptions();
  }, [fetchProfile, fetchSubscriptions]);

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#FFF0F3] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-rose-500"></div>
      </div>
    );

  if (!user)
    return (
      <div className="min-h-screen bg-[#FFF0F3] flex items-center justify-center font-bold">
        Please login to view profile.
      </div>
    );
  const hasUnusedActivePlan = userSubscriptions.some(
    (sub) => sub.is_active === 1 && sub.is_used === 0,
  );

  // It checks if current path is exactly '/package' AND user has an active plan
  const showHealthBenefits = user && Number(user.is_subscriber) === 1;
  return (
    <div className="min-h-screen bg-[#FFF0F3] py-12 px-6">
      <Toaster position="top-center" />

      <AnimatePresence>
        {isEditModalOpen && (
          <EditProfileModal
            user={user}
            onClose={() => setIsEditModalOpen(false)}
            onUpdate={fetchProfile}
            getImageUrl={getFullImageUrl}
          />
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto space-y-8">
        {/* 1. Header Profile Section - (Kept your style, refined padding) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[2.5rem] p-6 md:p-10 shadow-2xl shadow-rose-200/40 border border-white flex flex-col md:flex-row items-center gap-8 relative overflow-hidden"
        >
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-rose-50/50 rounded-full z-0" />

          <div className="relative z-10">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-[2rem] flex items-center justify-center bg-rose-500 text-white text-5xl font-black shadow-xl border-4 border-white overflow-hidden">
              {user.profile_image && !imgError ? (
                <img
                  src={getFullImageUrl(user.profile_image)}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                <span className="select-none">
                  {user.user_name?.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>

          <div className="relative z-10 flex-1 text-center md:text-left">
            <div className="flex flex-col items-center md:items-start gap-2">
              <span className="bg-rose-100 text-rose-600 text-[10px] font-black uppercase tracking-[0.2em] px-4 py-1.5 rounded-full">
                {user.account_type || "Member"}
              </span>
              <h1 className="text-4xl font-black text-rose-950 uppercase tracking-tighter">
                {user.user_name}
              </h1>
              <p className="text-rose-900/40 font-bold flex items-center gap-2">
                <HiOutlineEnvelope className="text-rose-500" size={18} />{" "}
                {user.email}
              </p>
            </div>
          </div>

          <button
            onClick={() => setIsEditModalOpen(true)}
            className="relative z-10 bg-rose-950 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-rose-800 transition-all hover:scale-105 active:scale-95 shadow-lg"
          >
            Edit Profile
          </button>
        </motion.div>

        {/* 2. Info Grid: Personal & Security Side-by-Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Details */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white/70 backdrop-blur-md border border-white p-8 rounded-[2.5rem] shadow-xl"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-rose-500 rounded-lg text-white">
                <HiOutlineUser size={20} />
              </div>
              <h3 className="text-rose-950 font-black uppercase tracking-widest text-sm">
                Personal Info
              </h3>
            </div>
            <div className="space-y-6">
              <InfoItem
                icon={<RiGenderlessLine />}
                label="Gender"
                value={user.gender || "Not specified"}
              />
              <InfoItem
                icon={<HiCake />}
                label="Birthday"
                value={formatDate(user.date_of_birth)}
              />
              <InfoItem
                icon={<HiOutlineCalendar />}
                label="Joined Platform"
                value={formatDate(user.created_date)}
              />
            </div>
          </motion.div>

          {/* Contact & Security */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white/70 backdrop-blur-md border border-white p-8 rounded-[2.5rem] shadow-xl"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-rose-500 rounded-lg text-white">
                <HiOutlineLockClosed size={20} />
              </div>
              <h3 className="text-rose-950 font-black uppercase tracking-widest text-sm">
                Security
              </h3>
            </div>
            <div className="space-y-6">
              <InfoItem
                icon={<HiOutlinePhone />}
                label="Primary Mobile"
                value={user.mobile_number}
              />

              {/* Password Item with Toggle */}
              <div className="flex items-center justify-between group bg-rose-50/30 p-3 rounded-2xl border border-rose-100/50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-rose-500 shadow-sm">
                    <HiOutlineLockClosed size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-rose-300 uppercase">
                      Password
                    </p>
                    <p className="text-rose-950 font-bold tracking-widest">
                      {showPassword ? user.password : "••••••••"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-2 hover:bg-rose-500 hover:text-white rounded-xl text-rose-400 transition-all"
                >
                  {showPassword ? (
                    <HiOutlineEyeSlash size={18} />
                  ) : (
                    <HiOutlineEye size={18} />
                  )}
                </button>
              </div>
              <InfoItem
                icon={<VscReferences />}
                label="Referral Code"
                value={user.user_code}
              />
            </div>
          </motion.div>
        </div>

        {/* 3. Unified Address Book Section */}
        <motion.div>
          <UserProfileAddress userId={getUserId()} />
        </motion.div>

        {showHealthBenefits && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 overflow-hidden bg-white rounded-3xl shadow-md border border-pink-200 flex flex-col md:flex-row items-center cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() =>
              window.open(
                "https://booking.thyrocare.com/landing-page?pageId=3719320c2d944f9eabad512a11769691280269a40358c8256b4d21acb4cefc53",
                "_blank",
              )
            }
          >
            <div className="p-6 md:w-1/3 flex justify-center bg-gray-50">
              <img
                src={thyrocareImg}
                alt="Thyrocare Partnership"
                className="h-20 object-contain"
              />
            </div>
            <div className="p-6 md:w-2/3 text-center md:text-left">
              <h3 className="text-xl font-bold text-pink-700 mb-1">
                Exclusive Health Benefit!
              </h3>
              <p className="text-gray-600 mb-3">
                As a valued subscriber, you can now access specialized health
                screenings at discounted rates through our partner, Thyrocare.
              </p>
              <span className="text-pink-600 font-semibold underline">
                Click here to book your test now →
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};
const formatToPickerDate = (dateString) => {
  if (!dateString) return "";
  
  // Create a date object
  const date = new Date(dateString);
  
  // If the date is invalid, return empty
  if (isNaN(date.getTime())) return "";

  // Use getFullYear, getMonth, and getDate (Local Time) 
  // to prevent the 1-day timezone shift
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`; // Returns YYYY-MM-DD
};
// --- EDIT MODAL COMPONENT ---
const EditProfileModal = ({ user, onClose, onUpdate, getImageUrl }) => {
  const formatToISODate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    // Use local methods (getFullYear, getMonth, getDate)
    // instead of UTC methods to prevent the 1-day timezone shift
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };
  const [formData, setFormData] = useState({
    ...user,
    date_of_birth: formatToPickerDate(user.date_of_birth),
  });
  // Calculate date 18 years ago from today
  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  )
    .toISOString()
    .split("T")[0]; // Result: YYYY-MM-DD
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(getImageUrl(user.profile_image));
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) return toast.error("Image Max 5MB");
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const triggerUpdateToast = (userName) => {
    toast.custom((t) => (
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={t.visible ? { opacity: 1, y: 0 } : { opacity: 0 }}
        className="max-w-md w-full bg-white shadow-2xl rounded-3xl p-4 flex items-center border-b-4 border-rose-500"
      >
        <div className="h-12 w-12 rounded-xl bg-rose-500 flex items-center justify-center text-white">
          <HiOutlineSparkles size={24} />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-black text-rose-950 uppercase">
            Profile Updated!
          </p>
          <p className="text-xs font-medium text-rose-900/40">
            Changes saved for {userName}!
          </p>
        </div>
        <button
          onClick={() => toast.dismiss(t.id)}
          className="ml-4 text-rose-500 font-bold text-xs uppercase"
        >
          Close
        </button>
      </motion.div>
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.date_of_birth) {
      const selectedDate = new Date(formData.date_of_birth);
      const limitDate = new Date(eighteenYearsAgo);

      if (selectedDate > limitDate) {
        toast.error("You must be at least 18 years old.");
        setLoading(false);
        return; // Stop submission
      }
    }
       if (formData.date_of_birth) {
        if (new Date(formData.date_of_birth) > new Date(eighteenYearsAgo)) {
            toast.error("You must be at least 18 years old.");
            return;
        }
    }
    setLoading(true);
    try {
      const data = new FormData();

      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && key !== "profile_image") {
          data.append(key, formData[key]);
        }
      });

      if (imageFile) {
        data.append("profile_image", imageFile);
      }

      const response = await fetch(SummaryApi.updateUser.url, {
        method: SummaryApi.updateUser.method,
        body: data,
      });

      const result = await response.json();
      if (result.status) {
        triggerUpdateToast(formData.user_name);
        onUpdate();
        onClose();
      } else {
        toast.error(result.message || "Failed to update");
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-rose-950/40 backdrop-blur-sm"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[3rem] shadow-2xl p-8 md:p-12"
      >
        <button
          onClick={onClose}
          className="absolute top-8 right-8 p-2 hover:bg-rose-50 rounded-full text-rose-300 transition-colors"
        >
          <HiOutlineXMark size={28} />
        </button>
        <h2 className="text-3xl font-black text-rose-950 uppercase tracking-tighter mb-8">
          Edit Profile
        </h2>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="flex flex-col items-center gap-4">
            <div
              className="relative group cursor-pointer"
              onClick={() => fileInputRef.current.click()}
            >
              <div className="w-32 h-32 rounded-[2.5rem] bg-rose-50 overflow-hidden border-4 border-white shadow-xl">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl font-black text-rose-200">
                    {formData.user_name?.charAt(0)}
                  </div>
                )}
              </div>
              <div className="absolute inset-0 bg-rose-950/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-[2.5rem] flex items-center justify-center text-white">
                <HiOutlineCamera size={32} />
              </div>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/*"
              className="hidden"
            />
            <p className="text-[10px] font-black text-rose-400 uppercase tracking-widest">
              Update Photo
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputGroup
              label="Full Name"
              name="user_name"
              value={formData.user_name}
              onChange={handleChange}
            />

            {/* EMAIL - DISABLED */}
            <InputGroup
              label="Email Address (Locked)"
              name="email"
              type="email"
              value={formData.email}
              disabled
            />

            {/* MOBILE - DISABLED */}
            <InputGroup
              label="Mobile Number (Locked)"
              name="mobile_number"
              value={formData.mobile_number}
              disabled
            />

            <InputGroup
              label="Account Password"
              name="password"
              type="text"
              value={formData.password}
              onChange={handleChange}
            />
            <InputGroup
              label="Date of Birth"
              name="date_of_birth"
              type="date"
              value={formData.date_of_birth}
              onChange={handleChange}
              max={eighteenYearsAgo} // Set max date to ensure user is at least 18 years old
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">
                Gender
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full bg-rose-50/50 border border-rose-100 rounded-2xl p-4 text-rose-950 font-bold focus:outline-none focus:ring-2 focus:ring-rose-200 transition-all"
              >
                <option value="Female">Female</option>
                <option value="Male">Male</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <InputGroup
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
            />
            <div className="md:col-span-2">
              <InputGroup
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </div>
          </div>

          <button
            disabled={loading}
            type="submit"
            className="w-full bg-rose-500 hover:bg-rose-600 text-white font-black py-5 rounded-2xl shadow-xl transition-all flex items-center justify-center"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-white" />
            ) : (
              "Save Changes"
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

// Helper component for the modal
const FormInput = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  ...props // 1. Collect all other props like type, maxLength, etc.
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">
      {label}
    </label>
    <input
      {...props} // 2. Spread those props onto the input
      required={required}
      placeholder={placeholder}
      className="w-full bg-rose-50/50 border border-rose-100 rounded-xl p-3 text-sm text-rose-950 font-bold outline-none focus:ring-2 ring-rose-200 placeholder:text-rose-200"
      value={value}
      onChange={(e) => onChange(e.target.value)} // 3. This extracts the string
    />
  </div>
);
// --- Updated InputGroup Helper with Disabled Styles ---
const InputGroup = ({ label, ...props }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">
      {label}
    </label>
    <input
      className="w-full bg-rose-50/50 border border-rose-100 rounded-2xl p-4 text-rose-950 font-bold focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
      {...props}
    />
  </div>
);

const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-4">
    <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center text-rose-500 shrink-0">
      {React.cloneElement(icon, { size: 20 })}
    </div>
    <div>
      <p className="text-[10px] font-black text-rose-300 uppercase tracking-[0.1em]">
        {label}
      </p>
      <p className="text-rose-950 font-bold mt-0.5">{value}</p>
    </div>
  </div>
);

export default UserProfile;
